import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { friendlyAuthError } from "../../auth/authErrors";
import type { AuthProviderConfig } from "../../auth/authProviders";
import B2cForm from "./B2cForm";
import { MicrosoftGlyph, ProviderIcon, Spinner } from "./icons";

/**
 * Advisory sign-in modal for a NON-Atlas provider.
 *
 * Providers kept telling us the three-way sign-in picker was confusing: it
 * offered "Astrana SSO" and "Pathways account" with the same visual weight as
 * the Atlas credentials they actually have, with nothing saying who each one
 * is for. The Atlas form is now the login card itself, and these two live
 * behind tiles underneath — clicking one opens this modal, which leads with
 * who the method is for and points everyone else back to the Atlas form.
 *
 * MUST be portalled to <body>, not rendered inline. A `fixed inset-0` overlay
 * inside the component tree gets trapped by any ancestor that establishes a
 * containing block (`transform` / `filter` / `contain`), and the background
 * subtree gets promoted to a GPU layer and re-rasterised — every font behind
 * the overlay visibly shifts while it is open. See CLAUDE.md / PR #122.
 *
 * Distinct from auth/PpLoginModal.tsx, which looks similar but does a
 * different job: that one acquires a Provider Portal token mid-session
 * against a LOCKED identity and deliberately never touches AuthContext. This
 * one is a plain login and is freely dismissable, because taking it is the
 * user's choice rather than a step they are blocked on.
 */

interface Advisory {
  title: string;
  body: string;
}

/**
 * Deliberately not phrased as "internal use only" — these are working sign-in
 * methods, not a warning. Lead with who the method IS for, then point
 * everyone else back to the Atlas form.
 */
const ADVISORY: Record<string, Advisory> = {
  entra: {
    title: "Astrana SSO",
    body: "For Astrana Health employees signing in with their corporate account. If you are a provider or office staff, go back and sign in with your Atlas credentials.",
  },
  "pathways-b2c": {
    title: "Pathways account",
    body: "For Astrana Health staff with a Pathways account. If you are a provider or office staff, go back and sign in with your Atlas credentials.",
  },
};

export interface SecondaryLoginModalProps {
  provider: AuthProviderConfig;
  onClose: () => void;
  onEntra: () => Promise<void>;
  onB2c: (email: string, password: string) => Promise<void>;
}

export default function SecondaryLoginModal({
  provider,
  onClose,
  onEntra,
  onB2c,
}: SecondaryLoginModalProps) {
  const [entraPending, setEntraPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  // Restore focus to whatever opened the modal (the tile) when it closes.
  const openerRef = useRef<Element | null>(null);

  useEffect(() => {
    openerRef.current = document.activeElement;
    dialogRef.current?.focus();
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (openerRef.current instanceof HTMLElement) openerRef.current.focus();
    };
  }, [onClose]);

  const advisory = ADVISORY[provider.id] ?? {
    title: provider.label,
    body: "If you are a provider or office staff, go back and sign in with your Atlas credentials.",
  };

  async function runEntra() {
    setError(null);
    setEntraPending(true);
    try {
      await onEntra();
    } catch (e) {
      setError(friendlyAuthError(e, "Sign in failed. Please try again."));
      setEntraPending(false);
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 backdrop-blur-sm p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="secondary-login-title"
        tabIndex={-1}
        className="w-full max-w-sm bg-card text-card-foreground rounded-2xl border border-border/60 p-7 flex flex-col gap-5 shadow-[0_24px_50px_-20px_rgba(0,0,0,0.35)] focus:outline-none"
      >
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2.5 text-primary dark:text-[hsl(324,60%,70%)]">
            {provider.id === "entra" ? (
              <MicrosoftGlyph size={18} />
            ) : (
              <ProviderIcon id={provider.id} size={18} />
            )}
            <h2
              id="secondary-login-title"
              className="text-lg font-semibold m-0 font-display text-foreground"
            >
              {advisory.title}
            </h2>
          </div>
          <p className="text-sm text-muted-foreground m-0 leading-relaxed">
            {advisory.body}
          </p>
        </div>

        {provider.id === "entra" ? (
          <div className="flex flex-col gap-3">
            {entraPending ? (
              <div className="flex flex-col items-center gap-3 py-2">
                <Spinner />
                <p className="text-sm text-muted-foreground m-0">
                  Redirecting you to sign in…
                </p>
              </div>
            ) : (
              <button
                type="button"
                onClick={runEntra}
                className="w-full inline-flex items-center justify-center gap-3 px-5 py-3 rounded-xl text-white bg-gradient-to-br from-[#8a1558] to-[#c42a83] hover:brightness-110 hover:shadow-[0_4px_14px_rgba(138,21,88,0.3)] text-sm font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <MicrosoftGlyph />
                <span>Continue with Astrana SSO</span>
              </button>
            )}
            {error && (
              <p
                role="alert"
                className="text-sm text-destructive font-medium m-0 text-center"
              >
                {error}
              </p>
            )}
          </div>
        ) : (
          <B2cForm variant="secondary" onSubmit={onB2c} />
        )}

        <button
          type="button"
          onClick={onClose}
          className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          ← Back to Atlas sign-in
        </button>
      </div>
    </div>,
    document.body,
  );
}
